/**
 * Authentication state and WebAuthn passkey operations.
 * Singleton: all components share the same user/loading/error refs.
 * Call checkSession() once on app mount to hydrate from the session cookie.
 */
import { ref, computed, readonly } from 'vue'
import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
} from '@simplewebauthn/browser'

export interface Passkey {
  id: string
  name: string | null
  deviceType: string | null
  backedUp: boolean
  createdAt: string | null
  lastUsedAt: string | null
}

export interface AuthUser {
  id: string
  email: string
  name: string | null
  avatarUrl?: string | null
}

const user = ref<AuthUser | null>(null)
const loading = ref(true)
const error = ref<string | null>(null)

async function apiFetch(path: string, options?: RequestInit): Promise<Response> {
  return fetch(path, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...options?.headers },
  })
}

export function useAuth() {
  const isAuthenticated = computed(() => !!user.value)

  async function checkSession(): Promise<void> {
    loading.value = true
    try {
      const res = await apiFetch('/api/auth/me')
      const data = await res.json()
      user.value = data.user ?? null
    } catch {
      user.value = null
    } finally {
      loading.value = false
    }
  }

  async function register(email: string, name: string): Promise<boolean> {
    error.value = null
    if (!browserSupportsWebAuthn()) {
      error.value = 'Your browser does not support passkeys. Try Chrome, Safari, or Firefox.'
      return false
    }

    const startRes = await apiFetch('/api/auth/register/start', {
      method: 'POST',
      body: JSON.stringify({ email, name }),
    })
    if (!startRes.ok) {
      const data = await startRes.json()
      error.value = data.error ?? 'Registration failed'
      return false
    }
    const options = await startRes.json()

    let credential
    try {
      credential = await startRegistration({ optionsJSON: options })
    } catch (e) {
      error.value =
        e instanceof Error && e.name === 'NotAllowedError'
          ? 'Passkey creation was cancelled.'
          : 'Passkey creation failed. Please try again.'
      return false
    }

    const verifyRes = await apiFetch('/api/auth/register/verify', {
      method: 'POST',
      body: JSON.stringify(credential),
    })
    if (!verifyRes.ok) {
      const data = await verifyRes.json()
      error.value = data.error ?? 'Registration failed'
      return false
    }
    const result = await verifyRes.json()
    user.value = result.user
    return true
  }

  async function login(email?: string): Promise<boolean> {
    error.value = null
    if (!browserSupportsWebAuthn()) {
      error.value = 'Your browser does not support passkeys. Try Chrome, Safari, or Firefox.'
      return false
    }

    const startRes = await apiFetch('/api/auth/login/start', {
      method: 'POST',
      body: JSON.stringify(email ? { email } : {}),
    })
    if (!startRes.ok) {
      const data = await startRes.json()
      error.value = data.error ?? 'Sign in failed'
      return false
    }
    const options = await startRes.json()

    let credential
    try {
      credential = await startAuthentication({ optionsJSON: options })
    } catch (e) {
      error.value =
        e instanceof Error && e.name === 'NotAllowedError'
          ? 'Sign in was cancelled.'
          : 'Sign in failed. Please try again.'
      return false
    }

    const verifyRes = await apiFetch('/api/auth/login/verify', {
      method: 'POST',
      body: JSON.stringify(credential),
    })
    if (!verifyRes.ok) {
      const data = await verifyRes.json()
      error.value = data.error ?? 'Sign in failed'
      return false
    }
    const result = await verifyRes.json()
    user.value = result.user
    return true
  }

  async function logout(): Promise<void> {
    await apiFetch('/api/auth/logout', { method: 'POST' })
    user.value = null
  }

  /** Uploads a file to R2 via the images endpoint and updates local avatar state. */
  async function uploadAvatar(file: File): Promise<boolean> {
    error.value = null
    const formData = new FormData()
    formData.append('file', file)
    const res = await fetch('/api/images/avatar', { method: 'PUT', body: formData })
    if (!res.ok) {
      const data = await res.json()
      error.value = (data as { error?: string }).error ?? 'Upload failed'
      return false
    }
    const { url } = (await res.json()) as { url: string }
    if (user.value) user.value = { ...user.value, avatarUrl: url }
    return true
  }

  /** Updates name and/or avatarUrl on the server and reflects changes in local state. */
  async function updateProfile(updates: { name?: string; avatarUrl?: string }): Promise<boolean> {
    error.value = null
    const body: Record<string, string> = {}
    if (updates.name !== undefined) body.name = updates.name
    if (updates.avatarUrl !== undefined) body.avatar_url = updates.avatarUrl

    const res = await apiFetch('/api/users/me', {
      method: 'PUT',
      body: JSON.stringify(body),
    })
    if (!res.ok) {
      const data = await res.json()
      error.value = (data as { error?: string }).error ?? 'Update failed'
      return false
    }
    const updated = (await res.json()) as { name: string | null; avatarUrl?: string | null }
    if (user.value) {
      user.value = { ...user.value, name: updated.name, avatarUrl: updated.avatarUrl ?? null }
    }
    return true
  }

  async function getPasskeys(): Promise<Passkey[]> {
    const res = await apiFetch('/api/auth/passkeys')
    if (!res.ok) return []
    const data = (await res.json()) as { passkeys: Passkey[] }
    return data.passkeys
  }

  /** Removes a passkey by id. Returns null on success, an error string on failure. */
  async function deletePasskey(id: string): Promise<string | null> {
    const res = await apiFetch(`/api/auth/passkeys/${id}`, { method: 'DELETE' })
    if (!res.ok) {
      const data = (await res.json()) as { error?: string }
      return data.error ?? 'Failed to remove passkey'
    }
    return null
  }

  return {
    user: readonly(user),
    loading: readonly(loading),
    error,
    isAuthenticated,
    checkSession,
    register,
    login,
    logout,
    updateProfile,
    uploadAvatar,
    getPasskeys,
    deletePasskey,
  }
}
